// third-party
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../index';
import apiSlice from './apiSlice';

const initialState = {
    error: null,
    uploadedFile: null,
    isUploaded: false,
    isSuccess: false
};

const slice = createSlice({
    name: 'uploadedFile',
    initialState,
    reducers: {
        getUploadedFile(state, action) {
            state.uploadedFile = Array.isArray(action.payload) ? action.payload : [];
        },
        getIsUploaded(state, action) {
            state.isUploaded = action.payload;
        },
        hasSuccess(state, action) {
            state.isSuccess = action.payload;
        },
        hasError(state, action) {
            state.error = action.payload;
        }
    }
});

export default slice.reducer;

function parseFolderPath(folderPath) {
    if (folderPath && folderPath.includes('/')) {
        const pathParts = folderPath.split('/');
        return { project_name: pathParts[0], folder_path: pathParts.slice(1).join('/') };
    }
    return { project_name: folderPath || '', folder_path: '' };
}

function mapDocumentToUploadedFile(data, index) {
    return {
        id: index,
        file_name: data.file_name,
        file_path: data.file_path,
        file_size: data.file_size,
        file_type: data.file_type,
        type: data.type,
        labels: data.labels || '',
        created_date: data.created_date,
        crawl_status: 'Unknown',
        index_status: 'Unknown',
        word_num: 'Unknown'
    };
}

export function fetchUploadedFiles(folderPath) {
    return async (dispatch) => {
        try {
            const { project_name, folder_path } = parseFolderPath(folderPath);
            const result = await dispatch(
                apiSlice.endpoints.getProjectDocuments.initiate({ project_name, folder_path })
            ).unwrap();
            const list = Array.isArray(result) ? result : [];
            const mappedData = list.map((file, index) => mapDocumentToUploadedFile(file, index));
            dispatch(slice.actions.getUploadedFile(mappedData));
            dispatch(slice.actions.hasSuccess(true));
        } catch (error) {
            console.error('fetchUploadedFiles error:', error);
            dispatch(slice.actions.hasSuccess(false));
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function deleteUploadedFiles(project_name, folder_name, file_name) {
    return async (dispatch) => {
        try {
            const formData = new FormData();
            const user_token = localStorage.getItem('user_token');
            formData.append('user_token', user_token);
            formData.append('project_name', project_name);
            formData.append('folder_name', folder_name);
            formData.append('file_name', file_name);
            await dispatch(apiSlice.endpoints.deleteProjectDocument.initiate(formData)).unwrap();
            dispatch(slice.actions.hasSuccess(true));
        } catch (error) {
            dispatch(slice.actions.hasSuccess(false));
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function removeUploadedFiles() {
    return () => {
        dispatch(slice.actions.getUploadedFile(null));
    };
}

export function setIsUploadedFiles(result) {
    return () => {
        dispatch(slice.actions.getIsUploaded(result));
    };
}

export function viewFileContent(projectName, filePath) {
    return async (dispatch) => {
        try {
            const result = await dispatch(
                apiSlice.endpoints.viewProjectFile.initiate({ project_name: projectName, file_path: filePath })
            ).unwrap();
            dispatch(slice.actions.hasSuccess(true));
            return result;
        } catch (error) {
            console.error('Error viewing file:', error);
            dispatch(slice.actions.hasSuccess(false));
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}

export function downloadFile(projectName, filePath, fileName, openInNewTab = false) {
    return async (dispatch) => {
        try {
            const blob = await dispatch(
                apiSlice.endpoints.getProjectUploadBlob.initiate({ project_name: projectName, file_path: filePath })
            ).unwrap();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            if (openInNewTab) {
                window.open(downloadUrl, '_blank').focus();
            } else {
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
            setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 100);
            dispatch(slice.actions.hasSuccess(true));
            return true;
        } catch (error) {
            console.error('Error downloading file:', error);
            dispatch(slice.actions.hasSuccess(false));
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}
